import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { teacherModuleAPI } from '../../services/api';

function resolveOption(catalog, moduleId, parameterId, optionId) {
  if (!optionId) return null;
  for (const cat of catalog || []) {
    for (const mod of cat.modules || []) {
      if (mod.id !== moduleId) continue;
      const param = (mod.parameters || []).find((p) => p.id === parameterId);
      const opt = (param?.options || []).find((o) => o.id === optionId);
      if (opt) return opt;
    }
  }
  return null;
}

function resolveOptionLabel(catalog, moduleId, parameterId, optionId) {
  const opt = resolveOption(catalog, moduleId, parameterId, optionId);
  if (opt?.label) return opt.label;
  return optionId || '—';
}

function resolveModuleName(catalog, moduleId, fallback) {
  for (const cat of catalog || []) {
    const mod = (cat.modules || []).find((m) => m.id === moduleId);
    if (mod?.name) return mod.name;
  }
  return fallback || 'Module';
}

function resolveParameterName(catalog, moduleId, parameterId, fallback) {
  for (const cat of catalog || []) {
    const mod = (cat.modules || []).find((m) => m.id === moduleId);
    const param = (mod?.parameters || []).find((p) => p.id === parameterId);
    if (param?.name) return param.name;
  }
  return fallback || 'Parameter';
}

export default function StudentAssessmentViewDialog({ open, reportId, onClose }) {
  const detailQuery = useQuery(
    ['studentAssessmentSubmission', reportId],
    async () => {
      const [detailRes, catalogRes] = await Promise.all([
        teacherModuleAPI.getStudentAssessmentSubmission(reportId),
        teacherModuleAPI.getStudentAssessmentCatalog(),
      ]);
      return {
        detail: detailRes.data?.data,
        catalog: catalogRes.data?.data || [],
      };
    },
    { enabled: open && !!reportId }
  );

  const detail = detailQuery.data?.detail;
  const catalog = detailQuery.data?.catalog || [];
  const report = detail?.report;
  const student = detail?.student;

  const rows = useMemo(() => {
    const modules = report?.assessmentData?.modules || [];
    const out = [];
    for (const mod of modules) {
      const sendCustomNotesOnly = Boolean(mod.sendCustomNotesOnly) && Boolean(String(mod.remarks || '').trim());
      for (const sel of mod.selections || []) {
        if (!sel.optionId) continue;
        out.push({
          key: `${mod.moduleId}-${sel.parameterId}`,
          moduleId: mod.moduleId,
          moduleName: mod.moduleName,
          parameterId: sel.parameterId,
          optionId: sel.optionId,
          remarks: null,
          sendCustomNotesOnly,
          isRemarks: false,
        });
      }
      if (String(mod.remarks || '').trim()) {
        out.push({
          key: `${mod.moduleId}-remarks`,
          moduleId: mod.moduleId,
          moduleName: mod.moduleName,
          parameterId: null,
          optionId: null,
          remarks: mod.remarks,
          sendCustomNotesOnly,
          isRemarks: true,
        });
      }
    }
    return out;
  }, [report]);

  const studentName = `${student?.firstName || ''} ${student?.lastName || ''}`.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Student monthly report</DialogTitle>
      <DialogContent dividers>
        {detailQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}
        {!detailQuery.isLoading && !detail ? (
          <Alert severity="error">Could not load this report.</Alert>
        ) : null}
        {detail ? (
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {studentName}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {detail.teacherName ? `Teacher: ${detail.teacherName} · ` : ''}
              {report?.reportMonth}
              {student?.batchName ? ` · ${student.batchName}` : ''}
              {student?.displayId ? ` · ${student.displayId}` : ''}
            </Typography>
            <Chip size="small" label={report?.status} sx={{ mb: 2 }} />
            {detail.teachingHint?.sessionsAttended != null ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sessions attended this month: {detail.teachingHint.sessionsAttended}
              </Typography>
            ) : null}
            {report?.status === 'rejected' && report.rejectionReason ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Rejection feedback: {report.rejectionReason}
              </Alert>
            ) : null}
            {rows.length === 0 ? (
              <Alert severity="info">No assessment rows recorded.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell>Parameter</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{resolveModuleName(catalog, row.moduleId, row.moduleName)}</TableCell>
                        <TableCell>
                          {row.parameterId
                            ? resolveParameterName(catalog, row.moduleId, row.parameterId)
                            : 'Remarks'}
                        </TableCell>
                        <TableCell>
                          {row.isRemarks ? (
                            <Box>
                              <Typography variant="body2">{row.remarks || '—'}</Typography>
                              {row.sendCustomNotesOnly ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Parents receive custom notes only (default explanation hidden)
                                </Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Parents receive default explanation + these notes
                                </Typography>
                              )}
                            </Box>
                          ) : row.optionId ? (
                            <Box>
                              <Typography variant="body2">
                                {resolveOptionLabel(catalog, row.moduleId, row.parameterId, row.optionId)}
                              </Typography>
                              {(() => {
                                if (row.sendCustomNotesOnly) return null;
                                const opt = resolveOption(
                                  catalog,
                                  row.moduleId,
                                  row.parameterId,
                                  row.optionId
                                );
                                return opt?.helpText ? (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {opt.helpText}
                                  </Typography>
                                ) : null;
                              })()}
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {report?.assessmentData?.overallRemarks ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Overall remarks</Typography>
                <Typography variant="body2">{report.assessmentData.overallRemarks}</Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
